/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { lazy } from "react";

// Lazy load all components for better performance
const Dashboard = lazy(() => import("./components/dashboard/Dashboard"));
const LiveCharting = lazy(() => import("./components/charting/LiveCharting"));
const ExchangeManagement = lazy(() => import("./components/exchanges/ExchangeManagement"));
const StrategyManagement = lazy(() => import("./components/strategies/StrategyManagement"));
const BotManagement = lazy(() => import("./components/bots/BotManagement"));
const BotDetail = lazy(() => import("./components/bots/BotDetail"));
const RiskManagement = lazy(() => import("./components/risk/RiskManagement"));
const Settings = lazy(() => import("./components/settings/Settings"));
const Users = lazy(() => import("./components/users/Users"));
const SystemHealth = lazy(() => import("./components/admin/SystemHealth"));
const ExchangeSymbolManagement = lazy(() => import("./components/admin/ExchangeSymbolManagement"));
const SecureConnectionManagement = lazy(
  () => import("./components/admin/SecureConnectionManagement")
);
const BacktestingPage = lazy(() => import("./components/backtesting/BacktestingPage"));
const AIPremium = lazy(() => import("./components/ai/AIPremium"));
const AISettings = lazy(() => import("./components/ai/AISettings"));
const AIBotManagement = lazy(() => import("./components/ai/AIBotManagement"));
const AIBotDetail = lazy(() => import("./components/ai/AIBotDetail"));
const ArbitrageBotManagement = lazy(() => import("./components/arbitrage/ArbitrageBotManagement"));
const ArbitrageBotDetail = lazy(() => import("./components/arbitrage/ArbitrageBotDetail"));
const NotificationSettings = lazy(() => import("./components/notifications/NotificationSettings"));
const DiscordSettings = lazy(() => import("./components/notifications/DiscordSettings"));
const WhatsAppSettings = lazy(() => import("./components/notifications/WhatsAppSettings"));
const TelegramSettings = lazy(() => import("./components/notifications/TelegramSettings"));
const EmailSettings = lazy(() => import("./components/notifications/EmailSettings"));
const SystemSettingsPage = lazy(() => import("./components/settings/SystemSettings"));
const AIPretrainDashboard = lazy(() => import("./components/admin/AIPretrainDashboard"));
const PricingPage = lazy(() => import("./components/subscription/PricingPage"));
const SubscriptionManagement = lazy(() => import("./components/subscription/SubscriptionManagement"));
const CheckoutSuccess = lazy(() => import("./components/subscription/CheckoutSuccess"));

const routes = [
  // ── Main ──────────────────────────────────────────────
  {
    type: "collapse",
    name: "dashboard",
    key: "dashboard",
    route: "/dashboard",
    icon: "dashboard",
    component: Dashboard,
    noCollapse: true,
  },

  // ── Trading ───────────────────────────────────────────
  {
    type: "collapse",
    name: "live charting",
    key: "live-charting",
    route: "/live-charting",
    icon: "show_chart",
    component: LiveCharting,
    noCollapse: true,
  },
  {
    type: "collapse",
    name: "signal bots",
    key: "bots",
    route: "/bots",
    icon: "smart_toy",
    component: BotManagement,
    noCollapse: true,
  },
  {
    type: "collapse",
    name: "AI bot",
    key: "ai-bot",
    route: "/ai-bot",
    icon: "psychology",
    component: AIBotManagement,
    noCollapse: true,
  },
  {
    type: "collapse",
    name: "arbitrage bot",
    key: "arbitrage-bot",
    route: "/arbitrage-bot",
    icon: "swap_horiz",
    component: ArbitrageBotManagement,
    noCollapse: true,
  },
  {
    type: "collapse",
    name: "backtesting",
    key: "backtesting",
    route: "/backtesting",
    icon: "analytics",
    component: BacktestingPage,
    noCollapse: true,
  },
  {
    type: "collapse",
    name: "strategies",
    key: "strategies",
    route: "/strategies",
    icon: "timeline",
    component: StrategyManagement,
    noCollapse: true,
  },

  // ── Configuration ─────────────────────────────────────
  {
    type: "collapse",
    name: "secure connections",
    key: "secure-connections",
    route: "/secure-connections",
    icon: "vpn_key",
    component: SecureConnectionManagement,
    noCollapse: true,
  },
  {
    type: "collapse",
    name: "AI premium",
    key: "ai",
    route: "/ai",
    icon: "ai",
    component: AIPremium,
    noCollapse: true,
  },
  {
    type: "collapse",
    name: "risk management",
    key: "risk",
    route: "/risk",
    icon: "security",
    component: RiskManagement,
    noCollapse: true,
  },
  {
    type: "collapse",
    name: "notifications",
    key: "notifications",
    route: "/notifications",
    icon: "notifications",
    component: NotificationSettings,
    noCollapse: false,
    collapse: [
      {
        type: "collapse",
        name: "discord",
        key: "discord",
        route: "/notifications/discord",
        component: DiscordSettings,
      },
      {
        type: "collapse",
        name: "whatsapp",
        key: "whatsapp",
        route: "/notifications/whatsapp",
        component: WhatsAppSettings,
      },
      {
        type: "collapse",
        name: "telegram",
        key: "telegram",
        route: "/notifications/telegram",
        component: TelegramSettings,
      },
      {
        type: "collapse",
        name: "email",
        key: "email",
        route: "/notifications/email",
        component: EmailSettings,
      },
    ],
  },
  {
    type: "collapse",
    name: "subscription",
    key: "subscription",
    route: "/subscription",
    icon: "subscription",
    component: SubscriptionManagement,
    noCollapse: true,
  },
  {
    type: "collapse",
    name: "settings",
    key: "settings",
    route: "/settings",
    icon: "settings",
    component: Settings,
    noCollapse: true,
  },

  // ── Admin (collapsible group) ─────────────────────────
  {
    type: "collapse",
    name: "administration",
    key: "administration",
    route: "/admin/system-settings",
    icon: "admin_panel_settings",
    component: SystemSettingsPage,
    adminOnly: true,
    noCollapse: false,
    collapse: [
      {
        type: "collapse",
        name: "system settings",
        key: "system-settings",
        route: "/admin/system-settings",
        component: SystemSettingsPage,
      },
      {
        type: "collapse",
        name: "AI settings",
        key: "ai-settings",
        route: "/admin/ai-settings",
        component: AISettings,
      },
      {
        type: "collapse",
        name: "system health",
        key: "system-health",
        route: "/admin/system-health",
        component: SystemHealth,
      },
      {
        type: "collapse",
        name: "exchanges",
        key: "exchanges",
        route: "/admin/exchanges",
        component: ExchangeManagement,
      },
      {
        type: "collapse",
        name: "exchange symbols",
        key: "exchange-symbols",
        route: "/admin/exchange-symbols",
        component: ExchangeSymbolManagement,
      },
      {
        type: "collapse",
        name: "AI pre-training",
        key: "ai-pretrain",
        route: "/admin/ai-pretrain",
        component: AIPretrainDashboard,
      },
      {
        type: "collapse",
        name: "users",
        key: "users",
        route: "/admin/users",
        component: Users,
      },
    ],
  },
];

// Bot detail route (not in sidenav, accessed via bot listing)
const botDetailRoute = {
  type: "route",
  name: "bot-detail",
  key: "bot-detail",
  route: "/bots/:botId/detail",
  component: BotDetail,
  noCollapse: true,
  hidden: true,
};

// AI bot detail route (not in sidenav)
const aiBotDetailRoute = {
  type: "route",
  name: "ai-bot-detail",
  key: "ai-bot-detail",
  route: "/ai-bot/:botId/detail",
  component: AIBotDetail,
  noCollapse: true,
  hidden: true,
};

// Arbitrage bot detail route (not in sidenav)
const arbBotDetailRoute = {
  type: "route",
  name: "arbitrage-bot-detail",
  key: "arbitrage-bot-detail",
  route: "/arbitrage-bot/:botId/detail",
  component: ArbitrageBotDetail,
  noCollapse: true,
  hidden: true,
};

// Pricing page (hidden from sidenav, accessed via subscription page or landing)
const pricingRoute = {
  type: "route",
  name: "pricing",
  key: "pricing",
  route: "/pricing",
  component: PricingPage,
  noCollapse: true,
  hidden: true,
};

// Checkout success page
const checkoutSuccessRoute = {
  type: "route",
  name: "checkout-success",
  key: "checkout-success",
  route: "/checkout/success",
  component: CheckoutSuccess,
  noCollapse: true,
  hidden: true,
};

// Add hidden detail routes to main routes array
const allRoutes = [...routes, botDetailRoute, aiBotDetailRoute, arbBotDetailRoute, pricingRoute, checkoutSuccessRoute];

export default allRoutes;
